import {
  CurrentUser,
  QBadge,
  QBodyLayout,
  QBox,
  QButton,
  QButtonGroup,
  QCheckbox,
  QEmptyState,
  QHeader,
  QHorizontalMetadata,
  QLastModifiedMetadata,
  QOwnerMetadata,
  QPageLoader,
  QStack,
  QTab,
  QTabList,
  QTabs,
  QTag,
  QTitle,
  QVersionMetadata,
  useCurrentUser,
  useFeatureFlag,
} from '@qualio/ui-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  createSearchParams,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { documentApi } from '../../../api/document';
import diff from '../../../node-htmldiff/htmldiff';
import { getVersionsToDocIdMap } from '../DocumentOverview/Buttons';
import {
  sortVersions,
  versionIsGreaterThan,
} from '../DocumentOverview/RenderLogic';

import {
  DocumentStatus,
  DocumentVersion,
  QualioDocument,
} from '../../../api/model/document';
import { useBodyBackground } from '../../../hooks/BodyBackground';
import { isBasicUserInAccount } from '../../../util/CurrentUser';
import { convertApiDocumentToViewableInstance } from '../../../util/DocumentApiUtils';
import { DocumentPresentationUtils } from '../../../util/DocumentPresentationUtils';
import { getOwnerText } from '../../../util/UserUtils';
import {
  DocumentStatusConfig,
  DocumentStatusIdToStatusConfigMap,
} from '../../../util/WorkspaceUtils';
import { BannerPlaceholder } from '../BannerPlaceholder';
import { DocumentCompareVersionsViewer } from '../DocumentCompareVersionsViewer/index';
import { CompareVersionsExportButton } from './CompareVersionsExportButton';
import './DocumentCompareVersions.module.css';
import { parseVersion } from './util';
import { VersionSelect } from './VersionSelect';

const CompareBody = ({
  children,
}: {
  children: React.ComponentProps<typeof QBodyLayout.Default>['children'];
}) => (
  <>
    <BannerPlaceholder />
    <QBodyLayout.Default>{children}</QBodyLayout.Default>
  </>
);

const TabPanelWrapper = ({
  children,
}: {
  children: React.ComponentProps<typeof QBox>['children'];
}) => <QBox pt="6">{children}</QBox>;

const DocumentHeader = ({
  leftDocument,
  rightDocument,
  documentStatusProps,
  onStopComparing,
  totalDiff,
  showStrikethrough,
}: {
  leftDocument: QualioDocument;
  rightDocument: QualioDocument;
  documentStatusProps: DocumentStatusConfig | undefined;
  onStopComparing: () => void;
  totalDiff: string;
  showStrikethrough: boolean;
}) => {
  const { t } = useTranslation();
  const { compareAgainstExport } = useFlags();

  return (
    <QHeader showDivider={false}>
      <QTitle>
        {leftDocument.title}{' '}
        <QBadge data-cy="document-code">{leftDocument.code}</QBadge>
      </QTitle>

      <QHorizontalMetadata>
        <QTag
          data-cy="document-status"
          variantColor={documentStatusProps?.color}
        >
          {documentStatusProps?.label}
        </QTag>
        <QVersionMetadata
          data-cy="document-version"
          value={leftDocument.major_version + '.' + leftDocument.minor_version}
        />
        <QOwnerMetadata
          data-cy="document-owner"
          value={getOwnerText(leftDocument)}
        />
        <QLastModifiedMetadata
          data-cy="document-last-modified"
          value={new Date(leftDocument.content_last_modified_at * 1000)}
        />
      </QHorizontalMetadata>

      <QButtonGroup>
        {compareAgainstExport && (
          <CompareVersionsExportButton
            qualioDocument={leftDocument}
            compareDocument={rightDocument}
            html={totalDiff}
            showStrikethrough={showStrikethrough}
          />
        )}
        <QButton onClick={onStopComparing}>
          {t('buttons.stopComparing')}
        </QButton>
      </QButtonGroup>
    </QHeader>
  );
};

const VersionSelectArea = ({
  currentUser,
  latestVersion,
  onLeftVersionChange,
  leftVersion,
  leftVersionOptions,
  onRightVersionChange,
  rightVersion,
  versions,
  showStrikethrough,
  onStrikethroughToggle,
}: {
  currentUser: CurrentUser;
  latestVersion: string;
  onLeftVersionChange: (version: string) => void;
  leftVersion: string | null;
  leftVersionOptions: DocumentVersion[];
  onRightVersionChange: (version: string) => void;
  rightVersion: string | undefined;
  versions: DocumentVersion[];
  showStrikethrough: boolean;
  onStrikethroughToggle: (checked: boolean) => void;
}) => {
  const { t } = useTranslation();

  return (
    <QStack direction="row">
      <QBox width={'100%'}>
        <QTabs variant="enclosed">
          <QTabList>
            <QTab data-cy="document-tab-compare-view" isDisabled={true}>
              {t('labels.document')}
            </QTab>

            <QStack direction="row" ml="auto" spacing="2">
              {!isBasicUserInAccount(currentUser) && (
                <VersionSelect
                  latestVersion={latestVersion}
                  handleOptionChange={onLeftVersionChange}
                  variant="LHS"
                  version={leftVersion}
                  allVersions={leftVersionOptions}
                />
              )}

              <VersionSelect
                latestVersion={latestVersion}
                handleOptionChange={onRightVersionChange}
                variant="RHS"
                version={rightVersion}
                allVersions={versions}
                LHSVersion={leftVersion}
              />
              <QCheckbox
                data-cy="accessibility-strikethrough-toggle"
                isChecked={showStrikethrough}
                onChange={(e) => onStrikethroughToggle(e.target.checked)}
                size="sm"
              >
                {t('versions.strikethroughDeletions')}
              </QCheckbox>
            </QStack>
          </QTabList>
        </QTabs>
      </QBox>
    </QStack>
  );
};

export const DocumentCompareVersions = () => {
  useBodyBackground('document-editor-overview-refresh', true);
  const { t } = useTranslation();
  const { id, baseCompare } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useCurrentUser();
  const { useLegacyExport, santitizeHtmlView } = useFlags();
  const rightVersion = baseCompare;
  const leftVersion = searchParams.get('version');
  const [showStrikethrough, setShowStrikethrough] = useState(false);
  const featureFlags = useFeatureFlag();
  const isDocumentCompareImprovementEnabled = Boolean(
    featureFlags['document-compare-improvement'],
  );

  const showIncompatibleVersionsState = useMemo(() => {
    if (rightVersion === leftVersion) {
      return false;
    }

    return (
      rightVersion &&
      leftVersion &&
      !versionIsGreaterThan(
        parseVersion(leftVersion),
        parseVersion(rightVersion),
      )
    );
  }, [rightVersion, leftVersion]);

  const location = useLocation();
  const navigate = useNavigate();

  const [totalDiff, setTotalDiff] = useState<string>('');

  const {
    data: versions = [],
    isLoading: isLoadingVersions,
    isFetching: isFetchingVersions,
  } = useQuery({
    queryKey: ['versions', id],
    queryFn: () => documentApi.getDocumentVersionsByDocumentId(Number(id!)),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const sortedVersions = useMemo(
    () => (versions ? sortVersions(versions) : []),
    [versions],
  );

  const latestVersion = useMemo(
    () =>
      sortedVersions.length > 0
        ? sortedVersions[sortedVersions.length - 1].version
        : '',
    [sortedVersions],
  );

  const loadDocument = useCallback(
    async (version: string | null | undefined) => {
      if (!id) {
        return;
      }

      const fetchedDocument = await documentApi.fetchByIdOrVersion(
        id,
        version ?? '',
        1,
        1,
      );

      return convertApiDocumentToViewableInstance(fetchedDocument);
    },
    [id],
  );

  const {
    isLoading: isLoadingLeftDocument,
    isFetching: isFetchingLeftDocument,
    data: leftDocument,
    error: leftDocumentError,
  } = useQuery({
    queryKey: ['newDocument', id, leftVersion],
    queryFn: () => loadDocument(leftVersion),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const {
    data: rightDocument,
    isLoading: isLoadingRightDocument,
    isFetching: isFetchingRightDocument,
    error: rightDocumentError,
  } = useQuery({
    queryKey: ['newDocument', id, rightVersion],
    queryFn: () => loadDocument(rightVersion),
    enabled: !!id,
    refetchOnWindowFocus: false,
  });

  const onLeftVersionChange = (version: string) => {
    if (!id) {
      return;
    }

    searchParams.set('version', version);
    const versionDocId = getVersionsToDocIdMap(versions)[version].toString();

    if (versionDocId === id) {
      setSearchParams(searchParams);
      return;
    }

    void navigate({
      pathname: location.pathname.replace(id, versionDocId),
      search: searchParams.toString(),
    });
  };

  const leftVersionOptions = useMemo(() => {
    return versions.filter((version) => version.version !== rightVersion);
  }, [versions, rightVersion]);

  const comparePathBase = () => location.pathname.split('/compare/')[0];

  const onRightVersionChange = (version: string) => {
    if (!id) {
      return;
    }

    void navigate({
      pathname: `${comparePathBase()}/compare/${version}`,
      search: leftVersion
        ? createSearchParams({ version: leftVersion }).toString()
        : '',
    });
  };

  const handleStopComparing = () => {
    if (!id) {
      return;
    }

    const pathname = comparePathBase();

    if (!leftVersion || leftVersion === latestVersion) {
      void navigate(pathname);
      return;
    }

    void navigate({
      pathname,
      search: createSearchParams({ version: leftVersion }).toString(),
    });
  };

  const handleStrikethroughToggle = (checked: boolean) => {
    setShowStrikethrough(checked);
  };

  const documentStatusProps = useMemo(() => {
    if (leftDocument?.status_id) {
      return DocumentStatusIdToStatusConfigMap[
        leftDocument.status_id as DocumentStatus
      ];
    }
  }, [leftDocument?.status_id]);

  useEffect(() => {
    const getHtmlContent = async () => {
      if (rightDocument && leftDocument) {
        const oldProcessedDocument =
          await DocumentPresentationUtils.determineHtmlDocumentContentFromDocument(
            rightDocument,
            false,
            true,
            useLegacyExport,
            santitizeHtmlView,
          );
        const latestProcessedDocument =
          await DocumentPresentationUtils.determineHtmlDocumentContentFromDocument(
            leftDocument,
            false,
            true,
            useLegacyExport,
            santitizeHtmlView,
          );
        setTotalDiff(
          diff(
            oldProcessedDocument,
            latestProcessedDocument,
            isDocumentCompareImprovementEnabled,
          ),
        );
      }
    };
    void getHtmlContent();
  }, [
    rightDocument,
    leftDocument,
    setTotalDiff,
    santitizeHtmlView,
    useLegacyExport,
    isDocumentCompareImprovementEnabled,
  ]);

  const versionSelectArea = (
    <VersionSelectArea
      currentUser={currentUser}
      latestVersion={latestVersion}
      onLeftVersionChange={onLeftVersionChange}
      leftVersion={leftVersion}
      leftVersionOptions={leftVersionOptions}
      onRightVersionChange={onRightVersionChange}
      rightVersion={rightVersion}
      versions={versions}
      showStrikethrough={showStrikethrough}
      onStrikethroughToggle={handleStrikethroughToggle}
    />
  );

  if (leftDocumentError || rightDocumentError) {
    return (
      <CompareBody>
        {versionSelectArea}

        <TabPanelWrapper>
          <QEmptyState
            title={t('versions.compareAgainstUnavailable')}
            subtitle={t('errors.errorLoadingDocumentAdjustVersion')}
          />
        </TabPanelWrapper>
      </CompareBody>
    );
  }

  if (
    !leftDocument ||
    !rightDocument ||
    isLoadingLeftDocument ||
    isFetchingLeftDocument ||
    isLoadingVersions ||
    isLoadingRightDocument ||
    isFetchingRightDocument ||
    isFetchingVersions
  ) {
    return (
      <>
        <BannerPlaceholder />
        <QPageLoader />
      </>
    );
  }

  return (
    <CompareBody>
      <DocumentHeader
        leftDocument={leftDocument}
        rightDocument={rightDocument}
        documentStatusProps={documentStatusProps}
        onStopComparing={handleStopComparing}
        totalDiff={totalDiff}
        showStrikethrough={showStrikethrough}
      />

      {versionSelectArea}

      {showIncompatibleVersionsState ? (
        <TabPanelWrapper>
          <QEmptyState
            title={t('errors.dontSupportThisCapability')}
            subtitle={t('errors.compareOlderVersionAgainstSelected')}
          />
        </TabPanelWrapper>
      ) : (
        <TabPanelWrapper>
          <QBox
            className={showStrikethrough ? 'accessibility-strikethrough' : ''}
          >
            <DocumentCompareVersionsViewer
              htmlContent={totalDiff}
              isLandscape={
                rightDocument.orientation === 'landscape' &&
                leftDocument.orientation === 'landscape'
              }
            />
          </QBox>
        </TabPanelWrapper>
      )}
    </CompareBody>
  );
};
